/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/EventThemeApplier.tsx
 *
 * Rôle :
 *   Applique À CHAUD la palette de l'événement actif (fondation
 *   multi-événement). Monté UNE fois dans `AppLayout`.
 *
 *   Mécanique zéro-régression : les `var(--tne-color-*, #hexTNE)` ne
 *   sont JAMAIS définies dans le CSS compilé (seul le fallback hex =
 *   palette TNE Vosges s'applique). Ici :
 *     - si l'événement a un `branding.palette` valide → on pose
 *       `--tne-color-<clé>` sur `:root` ⇒ tout le CSS bascule à chaud.
 *     - sinon (TNE par défaut / offline / V25 absente) → on ne pose
 *       RIEN ⇒ les fallbacks hex TNE Vosges restent ⇒ identique à
 *       aujourd'hui.
 *
 *   Ne rend aucun DOM (composant d'effet). Nettoie les variables au
 *   démontage / changement d'événement.
 *
 * Voir : hooks/useEventConfig.ts, styles/tokens.css,
 *        app/docs/MULTI-EVENT-ARCHITECTURE.md.
 *
 * @author Mika
 * @since  2026-05-16 (fondation multi-événement)
 */

import { useEffect } from 'react';
import { useEventConfig } from '@/hooks/useEventConfig';
import {
  assombrirPourContraste,
  SEUIL_AA_TEXTE,
} from '@/utils/contrasteWcag';

/** Clés de palette autorisées → nom de variable CSS appliquée. */
const PALETTE_TO_CSSVAR: Readonly<Record<string, string>> = {
  primary: '--tne-color-primary',
  accent: '--tne-color-accent',
  orange: '--tne-color-orange',
  cream: '--tne-color-cream',
  'text-strong': '--tne-color-text-strong',
  'on-primary': '--tne-color-on-primary',
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Surface claire la plus SOMBRE de l'application (la crème). On dérive la
 * couleur de texte contre elle : conforme sur la crème ⇒ conforme sur toutes
 * les surfaces plus claires (blanc, crème 2, fond global).
 */
const FOND_CLAIR_LE_PLUS_SOMBRE = '#F4F1EC';

/**
 * Variables dérivées de l'accent, posées EN PLUS de celles du branding.
 *
 * POURQUOI (chantier J, accessibilité 2026-09-09) :
 *   Un événement pose son accent de marque dans `branding_json`. Rien ne
 *   garantit que cet accent atteigne 4,5:1 en TEXTE (WCAG 1.4.3) ; celui de
 *   l'événement courant ne l'atteint pas. Si on se contentait du token compilé, changer
 *   d'événement remettrait des messages d'erreur illisibles.
 *   On recalcule donc la déclinaison texte à chaud, avec la MÊME fonction que
 *   le build : l'accessibilité suit l'identité au lieu de la freiner.
 */
const VARIABLES_TEXTE_DERIVEES: readonly string[] = [
  '--tne-color-accent-text',
  '--tne-color-error',
  '--tne-color-danger',
];

export function EventThemeApplier(): null {
  const { event } = useEventConfig();
  const palette = event.branding?.palette ?? null;

  useEffect(() => {
    if (!palette || typeof document === 'undefined') {
      return; // pas de branding → on garde le thème compilé (TNE)
    }
    const root = document.documentElement;
    const applied: string[] = [];

    for (const [key, cssVar] of Object.entries(PALETTE_TO_CSSVAR)) {
      const value = (palette as Record<string, string | undefined>)[key];
      if (typeof value === 'string' && HEX_RE.test(value)) {
        root.style.setProperty(cssVar, value);
        applied.push(cssVar);
      }
    }

    // Déclinaison ACCESSIBLE de l'accent (cf. VARIABLES_TEXTE_DERIVEES).
    const accent = (palette as Record<string, string | undefined>)['accent'];
    if (typeof accent === 'string' && HEX_RE.test(accent)) {
      const accentTexte = assombrirPourContraste(
        accent,
        FOND_CLAIR_LE_PLUS_SOMBRE,
        SEUIL_AA_TEXTE,
      );
      for (const cssVar of VARIABLES_TEXTE_DERIVEES) {
        root.style.setProperty(cssVar, accentTexte);
        applied.push(cssVar);
      }
      // L'anneau de focus partagé suit le même accent assombri : opaque et
      // visible (≥ 3:1, WCAG 1.4.11) quel que soit l'événement.
      root.style.setProperty('--shadow-focus', `0 0 0 3px ${accentTexte}`);
      applied.push('--shadow-focus');
    }

    return () => {
      // Restaure le thème compilé si l'événement change / démontage.
      for (const cssVar of applied) {
        root.style.removeProperty(cssVar);
      }
    };
  }, [palette]);

  return null;
}

export default EventThemeApplier;
